import { APP_ROUTES } from '../types/app-routes';
import { USER_ROLE, UserRoleType } from '../types/user-role.types';

/** Qué hace una fila de cuenta al tocarla. Lo ejecuta `AccountActionsService`. */
export type AccountActionId =
  | 'shortcut'
  | 'profile'
  | 'change-password'
  | 'preferences'
  | 'notifications'
  | 'account'
  | 'logout';

export interface AccountRow {
  /** Clave estable para el `track` del `@for`. */
  readonly id: string;
  readonly action: AccountActionId;
  readonly labelKey: string;
  /** Nombre de Ionicons. */
  readonly icon: string;
  /** Destino de las filas que navegan. Las acciones (cerrar sesión) no tienen. */
  readonly route?: string;
  /** Fila destructiva ("Cerrar sesión"): rojo y sin chevron. */
  readonly danger?: boolean;
}

export interface AccountGroup {
  readonly id: 'profile' | 'app' | 'session';
  readonly titleKey: string;
  readonly rows: readonly AccountRow[];
}

const ROW = {
  profile: { id: 'profile', action: 'profile', labelKey: 'account.cards.profile.title', icon: 'person-outline', route: APP_ROUTES.USER_PROFILE },
  changePassword: { id: 'change-password', action: 'change-password', labelKey: 'account.cards.change-password.title', icon: 'lock-closed-outline', route: APP_ROUTES.CHANGE_PASSWORD },
  preferences: { id: 'preferences', action: 'preferences', labelKey: 'account.cards.preferences.title', icon: 'options-outline', route: APP_ROUTES.INBOX_PREFERENCES },
  notifications: { id: 'notifications', action: 'notifications', labelKey: 'account.rows.notifications', icon: 'notifications-outline', route: APP_ROUTES.INBOX },
  account: { id: 'account', action: 'account', labelKey: 'account.rows.account', icon: 'person-circle-outline', route: APP_ROUTES.ACCOUNT },
  logout: { id: 'logout', action: 'logout', labelKey: 'account.rows.logout', icon: 'log-out-outline', danger: true },
} as const satisfies Record<string, AccountRow>;

const SHORTCUT = {
  sellerEvents: { id: 'events', action: 'shortcut', labelKey: 'navigation.navigate-options.events', icon: 'calendar-outline', route: APP_ROUTES.EVENTS_SELLER },
  smEvents: { id: 'events', action: 'shortcut', labelKey: 'navigation.navigate-options.events', icon: 'calendar-outline', route: APP_ROUTES.SALES_MANAGER_HOME },
  doorEvents: { id: 'events', action: 'shortcut', labelKey: 'navigation.navigate-options.events', icon: 'calendar-outline', route: APP_ROUTES.EVENTS_RECEPCIONIST },
  administration: { id: 'administration', action: 'shortcut', labelKey: 'navigation.navigate-options.administration', icon: 'people-outline', route: APP_ROUTES.ADMINISTRATION },
  appVersions: { id: 'app-versions', action: 'shortcut', labelKey: 'navigation.navigate-options.app-versions', icon: 'phone-portrait-outline', route: APP_ROUTES.APP_VERSION_ADMIN },
  turnstiles: { id: 'turnstile-management', action: 'shortcut', labelKey: 'navigation.navigate-options.turnstile-management', icon: 'hardware-chip-outline', route: APP_ROUTES.TURNSTILE_MANAGEMENT },
  qrStatus: { id: 'qr-status', action: 'shortcut', labelKey: 'navigation.navigate-options.qr-status', icon: 'qr-code-outline', route: APP_ROUTES.QR_STATUS },
} as const satisfies Record<string, AccountRow>;

/**
 * Accesos "Ir a" del sheet de cuenta: lo que antes daba el menú lateral.
 * "Eventos" va primero en todo rol que tiene pantalla de eventos (decisión del
 * usuario 2026-10-02), aunque el vendedor también lo tenga en su barra inferior:
 * desde una pantalla de detalle la barra no está. Admin Mobile no tiene eventos.
 * "Mi cuenta" vive en la sección de cuenta.
 */
const SHORTCUTS_BY_ROLE: Readonly<Record<UserRoleType, readonly AccountRow[]>> = {
  [USER_ROLE.SELLER]: [SHORTCUT.sellerEvents, SHORTCUT.qrStatus],
  [USER_ROLE.SALES_MANAGER]: [SHORTCUT.smEvents, SHORTCUT.administration, SHORTCUT.qrStatus],
  [USER_ROLE.ADMINISTRATOR]: [SHORTCUT.smEvents, SHORTCUT.administration, SHORTCUT.qrStatus],
  [USER_ROLE.SCANNER]: [SHORTCUT.doorEvents, SHORTCUT.administration, SHORTCUT.qrStatus],
  [USER_ROLE.RECEPCIONIST]: [SHORTCUT.doorEvents, SHORTCUT.qrStatus],
  [USER_ROLE.MOBILE_APP_ADMIN]: [SHORTCUT.appVersions, SHORTCUT.turnstiles, SHORTCUT.qrStatus],
  [USER_ROLE.GLOBAL_REPORT]: [],
  [USER_ROLE.UNKNOWN]: [],
};

/** Roles para los que no existe la pantalla de Preferencias. */
const ROLES_WITHOUT_PREFERENCES: readonly UserRoleType[] = [USER_ROLE.RECEPCIONIST];

export function initialsOf(fullname: string | null | undefined): string {
  return (fullname ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0].toUpperCase())
    .join('');
}

export function roleShortcuts(role: UserRoleType): AccountRow[] {
  return [...(SHORTCUTS_BY_ROLE[role] ?? [])];
}

/**
 * Accesos "Ir a" de un usuario `sellers`: no dependen del rol activo (ni dentro de un
 * evento con reporte global). Administración solo si es business_admin o jefe.
 */
export function sellersShortcuts(canAdminister: boolean): AccountRow[] {
  return [
    SHORTCUT.sellerEvents,
    ...(canAdminister ? [SHORTCUT.administration] : []),
    SHORTCUT.qrStatus,
  ];
}

export function accountSheetRows(): AccountRow[] {
  return [
    ROW.account,
    ROW.notifications,
    ROW.logout,
  ];
}

/** No hay "Cambiar rol": un usuario `sellers` cambia de rol desde el chip del evento. */
export function accountPageGroups(role: UserRoleType): AccountGroup[] {
  const hasPreferences = !ROLES_WITHOUT_PREFERENCES.includes(role);
  return [
    { id: 'profile', titleKey: 'account.sections.profile', rows: [ROW.profile, ROW.changePassword] },
    { id: 'app', titleKey: 'account.sections.app', rows: [...(hasPreferences ? [ROW.preferences] : []), ROW.notifications] },
    { id: 'session', titleKey: 'account.sections.session', rows: [ROW.logout] },
  ];
}
